import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle, XCircle, ScanBarcode, Camera, Search, Loader2, AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

/**
 * Per-USN Disposition dialog for the Damage Section.
 * Two outcomes:
 *   1) Cleared          -> replacement USN inherits original SKU/design/shade/size
 *   2) Damaged          -> mandatory damage types + photos + No Exchange item
 *
 * Both call POST /api/damage/dispose which fires the global USN replacement
 * engine. The piece stays OUT of FG inventory in either case — operator
 * must put away the new USN via the regular FG Put-Away screen.
 */
export default function DamageDispositionDialog({ open, onClose, row, onDone }) {
  const [tab, setTab] = useState('cleared'); // 'cleared' | 'damaged'
  const [submitting, setSubmitting] = useState(false);
  const [remarks, setRemarks] = useState('');

  // Damaged path state
  const [damageTypes, setDamageTypes] = useState([]);
  const [damageSearch, setDamageSearch] = useState('');
  const [selectedDamages, setSelectedDamages] = useState([]); // [{damage_code, damage_name}]
  const [photos, setPhotos] = useState([]); // [url]
  const [photoUploading, setPhotoUploading] = useState(false);

  const [neQuery, setNeQuery] = useState('');
  const [neItems, setNeItems] = useState([]);
  const [selectedNe, setSelectedNe] = useState(null);
  const [loadingNe, setLoadingNe] = useState(false);

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setTab('cleared');
    setRemarks('');
    setSelectedDamages([]);
    setPhotos([]);
    setSelectedNe(null);
    setDamageSearch('');
    setNeQuery('');
  }, [open]);

  // Lazy fetch damage types when entering Damaged tab
  useEffect(() => {
    if (open && tab === 'damaged' && damageTypes.length === 0) {
      apiClient.get('/damage/damage-types')
        .then(r => setDamageTypes(r.data?.damages || []))
        .catch(() => toast.error('Failed to load damage types'));
    }
  }, [open, tab, damageTypes.length]);

  // Search No Exchange items (debounced)
  useEffect(() => {
    if (!open || tab !== 'damaged') return;
    const t = setTimeout(async () => {
      setLoadingNe(true);
      try {
        const params = new URLSearchParams();
        if (neQuery.trim()) params.append('search', neQuery.trim());
        const r = await apiClient.get(`/damage/no-exchange-items?${params.toString()}`);
        setNeItems(r.data?.items || []);
      } catch {
        setNeItems([]);
      } finally {
        setLoadingNe(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [open, tab, neQuery]);

  const filteredDamageTypes = damageTypes.filter(d =>
    !damageSearch ||
    (d.damage_name || '').toLowerCase().includes(damageSearch.toLowerCase()) ||
    (d.damage_code || '').toLowerCase().includes(damageSearch.toLowerCase())
  );

  const toggleDamage = (d) => {
    setSelectedDamages(prev => {
      const found = prev.find(x => x.damage_code === d.damage_code);
      return found ? prev.filter(x => x.damage_code !== d.damage_code) : [...prev, d];
    });
  };

  const handlePhotoUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', `damage_section/${row?.usn_code || 'unknown'}`);
      const r = await apiClient.post('/storage/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = r.data?.url;
      if (url) {
        setPhotos(p => [...p, url]);
        toast.success('Photo uploaded');
      } else {
        toast.error('Upload failed (no URL returned)');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Photo upload failed');
    } finally {
      setPhotoUploading(false);
    }
  }, []);

  const handleSubmit = async () => {
    const cleared = tab === 'cleared';
    if (!cleared) {
      if (selectedDamages.length === 0) {
        toast.error('Select at least one damage type');
        return;
      }
      if (!selectedNe) {
        toast.error('Pick the No Exchange item');
        return;
      }
    }
    setSubmitting(true);
    try {
      const r = await apiClient.post('/damage/dispose', {
        usn_code: row.usn_code,
        cleared,
        remarks,
        damages: cleared ? null : selectedDamages,
        photo_urls: cleared ? null : photos,
        design_transfer_barcode: cleared ? null : selectedNe?.barcode,
        design_transfer_design_name: cleared ? null : selectedNe?.design_name,
        design_transfer_shade: cleared ? null : selectedNe?.shade,
        design_transfer_size: cleared ? null : selectedNe?.size,
      });
      const d = r.data || {};
      toast.success(d.message || `New USN: ${d.new_usn}`, { duration: 9000 });
      onDone?.(d);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Disposition failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!row) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="damage-disposition-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanBarcode className="w-5 h-5 text-amber-600" />
            Disposition — <span className="font-mono">{row.usn_code}</span>
          </DialogTitle>
          <DialogDescription className="space-x-2">
            <span className="text-xs text-gray-500">SKU {row.sku_code || '—'}</span>
            <span className="text-xs text-gray-500">·</span>
            <span className="text-xs text-gray-500">{row.design_name || '—'} / {row.shade || '—'} / {row.size || '—'}</span>
          </DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <div className="grid grid-cols-2 gap-2 my-2">
          <button
            type="button"
            onClick={() => setTab('cleared')}
            className={`p-3 rounded-md border-2 text-sm font-medium transition ${
              tab === 'cleared'
                ? 'bg-emerald-50 border-emerald-500 text-emerald-800'
                : 'bg-white border-gray-200 text-gray-600 hover:border-emerald-200'
            }`}
            data-testid="cleared-tab"
          >
            <CheckCircle className="w-4 h-4 inline mr-1" /> Cleared
            <p className="text-[11px] font-normal mt-0.5 opacity-75">Piece is OK after re-check</p>
          </button>
          <button
            type="button"
            onClick={() => setTab('damaged')}
            className={`p-3 rounded-md border-2 text-sm font-medium transition ${
              tab === 'damaged'
                ? 'bg-red-50 border-red-500 text-red-800'
                : 'bg-white border-gray-200 text-gray-600 hover:border-red-200'
            }`}
            data-testid="damaged-tab"
          >
            <XCircle className="w-4 h-4 inline mr-1" /> Damaged
            <p className="text-[11px] font-normal mt-0.5 opacity-75">Convert to No Exchange</p>
          </button>
        </div>

        {/* Cleared body */}
        {tab === 'cleared' && (
          <div className="space-y-3">
            <p className="text-xs bg-emerald-50 text-emerald-800 rounded-md p-3">
              A fresh USN will be claimed from the <b>REPLACE</b> pool with the
              original SKU / design / shade / size. The old USN is deactivated.
              Print the new label, stick it, then place the piece via the
              <b> Table Clearance</b> flow.
            </p>
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">Remarks (optional)</label>
              <Textarea
                rows={2}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="e.g. Re-checked, no defect on the piece"
                data-testid="cleared-remarks"
              />
            </div>
          </div>
        )}

        {/* Damaged body */}
        {tab === 'damaged' && (
          <div className="space-y-4">
            {/* Damage types */}
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">Damage type(s) *</label>
              <Input
                placeholder="Search damage types…"
                value={damageSearch}
                onChange={(e) => setDamageSearch(e.target.value)}
                className="mb-2 h-8"
                data-testid="damage-search"
              />
              <div className="max-h-40 overflow-y-auto border rounded-md p-2 grid grid-cols-2 gap-1 bg-gray-50">
                {filteredDamageTypes.length === 0 ? (
                  <p className="col-span-2 text-xs text-gray-400 py-2 text-center">No damage types match</p>
                ) : filteredDamageTypes.map(d => {
                  const checked = !!selectedDamages.find(x => x.damage_code === d.damage_code);
                  return (
                    <label
                      key={d.damage_code}
                      className={`flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer text-xs ${checked ? 'bg-red-100' : 'hover:bg-white'}`}
                      data-testid={`damage-opt-${d.damage_code}`}
                    >
                      <input type="checkbox" checked={checked} onChange={() => toggleDamage(d)} />
                      <span>{d.damage_name}</span>
                    </label>
                  );
                })}
              </div>
              {selectedDamages.length > 0 && (
                <p className="text-[11px] text-gray-500 mt-1">{selectedDamages.length} selected</p>
              )}
            </div>

            {/* Photos */}
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">Photos (optional)</label>
              <div className="flex flex-wrap gap-2 items-center">
                {photos.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                    <img src={url} alt="" className="w-14 h-14 rounded border object-cover" />
                  </a>
                ))}
                <label className="w-14 h-14 rounded border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:bg-gray-50 cursor-pointer" data-testid="photo-upload-btn">
                  {photoUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoUpload} />
                </label>
              </div>
            </div>

            {/* No Exchange selection */}
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">Convert to No Exchange item *</label>
              <div className="relative">
                <Search className="absolute left-2 top-2 w-4 h-4 text-gray-400" />
                <Input
                  className="pl-8 h-9"
                  placeholder="Search by barcode or design…"
                  value={neQuery}
                  onChange={(e) => setNeQuery(e.target.value)}
                  data-testid="no-exchange-search"
                />
              </div>
              {selectedNe && (
                <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-200 text-xs flex items-center justify-between" data-testid="no-exchange-selected">
                  <div>
                    <p className="font-mono font-bold">{selectedNe.barcode}</p>
                    <p className="text-amber-800">{selectedNe.design_name} / {selectedNe.shade} / {selectedNe.size}</p>
                  </div>
                  <button onClick={() => setSelectedNe(null)} className="text-amber-700 hover:text-red-600 underline text-[11px]">Change</button>
                </div>
              )}
              {!selectedNe && (
                <div className="max-h-40 overflow-y-auto border rounded-md mt-1 bg-white">
                  {loadingNe ? (
                    <p className="text-xs text-gray-400 p-3 text-center">Loading…</p>
                  ) : neItems.length === 0 ? (
                    <p className="text-xs text-gray-400 p-3 text-center">No No-Exchange items match</p>
                  ) : neItems.map(it => (
                    <button
                      type="button"
                      key={it.barcode}
                      onClick={() => setSelectedNe(it)}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-amber-50 border-b last:border-0"
                      data-testid={`ne-opt-${it.barcode}`}
                    >
                      <p className="font-mono font-semibold">{it.barcode}</p>
                      <p className="text-gray-500">{it.design_name} / {it.shade} / {it.size}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">Remarks (optional)</label>
              <Textarea
                rows={2}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Inspector note…"
                data-testid="damaged-remarks"
              />
            </div>

            <p className="text-[11px] text-gray-500 italic flex items-center gap-1">
              <AlertTriangle className="w-3 h-3 text-amber-500" />
              Both old and new USN are linked in the Admin USN Replacement Log + USN Tracker chain.
              After the new label is printed, place the piece via the <b>Table Clearance</b> flow.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose?.()} disabled={submitting} data-testid="dispose-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className={tab === 'cleared' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
            data-testid="dispose-confirm-btn"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {tab === 'cleared' ? 'Mark Cleared & Replace USN' : 'Mark Damaged → No Exchange'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
